import { useEffect, useState } from 'react';
import { Edit } from 'lucide-react';

const BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/products';

function getAuthHeaders() {
  const token = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('access_token'))
    || (typeof localStorage !== 'undefined' && localStorage.getItem('access_token'));
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ProductAvailability({ product, onSave }) {
  const [editMode, setEditMode] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Initialize state with recurring fields safely retrieved from the product data
  const [availability, setAvailability] = useState({
    purchase_type: product.availability?.purchase_type || product.purchase_type || 'one_off',
    purchase_method: product.availability?.purchase_method || product.purchase_method || 'buy_alone_or_with_plan',
    allowed_customers: product.availability?.allowed_customers || product.allowed_customers || 'all',
    recurring_config_frequency: product.availability?.recurring_config?.frequency || 'monthly',
    recurring_config_charge_amount: String(product.availability?.recurring_config?.charge_amount || '')
  });

  // Sync state when product updates without refreshing
  useEffect(() => {
    setAvailability({
      purchase_type: product.availability?.purchase_type || product.purchase_type || 'one_off',
      purchase_method: product.availability?.purchase_method || product.purchase_method || 'buy_alone_or_with_plan',
      allowed_customers: product.availability?.allowed_customers || product.allowed_customers || 'all',
      recurring_config_frequency: product.availability?.recurring_config?.frequency || 'monthly',
      recurring_config_charge_amount: String(product.availability?.recurring_config?.charge_amount || '')
    });
  }, [product]);

  const showOverlay = (message) => {
    setOverlayMessage(message);
    setIsOverlayOpen(true);
    setTimeout(() => setIsOverlayOpen(false), 3000);
  };

  const handleSave = async () => {
    try {
      // Base payload
      const payload = {
        availability: {
          purchase_type: availability.purchase_type,
          purchase_method: availability.purchase_method,
          allowed_customers: availability.allowed_customers
        }
      };

      // Append recurring config only if purchase type is recurring
      if (availability.purchase_type === 'recurring') {
        payload.availability.recurring_config = {
          frequency: availability.recurring_config_frequency,
          charge_amount: parseFloat(availability.recurring_config_charge_amount) || 0
        };
      }

      const response = await fetch(`${BASE_URL}/updateProducts/${product.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to update availability');
      
      setEditMode(false);
      await onSave();
      showOverlay('Availability updated successfully');
    } catch (error) {
      console.error('Error updating availability:', error);
      showOverlay('Failed to update availability: ' + error.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-md font-medium text-gray-900">Availability Details</h3>
        {!editMode ? (
          <button
            onClick={() => setEditMode(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setEditMode(false)}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Purchase Type
          </label>
          {editMode ? (
            <select
              value={availability.purchase_type}
              onChange={(e) => setAvailability({ ...availability, purchase_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="one_off">One-off</option>
              <option value="recurring">Recurring</option>
            </select>
          ) : (
            <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900 capitalize">
              {availability.purchase_type.replace('_', ' ')}
            </div>
          )}
        </div>

        {/* Dynamic Recurring Fields */}
        {availability.purchase_type === 'recurring' && (
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Frequency
              </label>
              {editMode ? (
                <select
                  value={availability.recurring_config_frequency}
                  onChange={(e) => setAvailability({ ...availability, recurring_config_frequency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              ) : (
                <div className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 capitalize">
                  {availability.recurring_config_frequency}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recurring Charge Amount
              </label>
              {editMode ? (
                <input
                  type="number"
                  value={availability.recurring_config_charge_amount}
                  onChange={(e) => setAvailability({ ...availability, recurring_config_charge_amount: e.target.value })}
                  placeholder="Enter amount"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-900">
                  {availability.recurring_config_charge_amount || '0'}
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Purchase Method
          </label>
          {editMode ? (
            <select
              value={availability.purchase_method}
              onChange={(e) => setAvailability({ ...availability, purchase_method: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="buy_alone_or_with_plan">Buy Alone or With Plan</option>
              <option value="buy_alone_only">Buy Alone Only</option>
              <option value="buy_only_if_plan_exists">Buy Only If Plan Exists</option>
            </select>
          ) : (
            <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900 capitalize">
              {availability.purchase_method.replace(/_/g, ' ')}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allowed Customers
          </label>
          {editMode ? (
            <select
              value={availability.allowed_customers}
              onChange={(e) => setAvailability({ ...availability, allowed_customers: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Customers</option>
              <option value="members">Members Only</option>
              <option value="contacts">Contacts Only</option>
            </select>
          ) : (
            <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900 capitalize">
              {availability.allowed_customers}
            </div>
          )}
        </div>
      </div>

      {/* Overlay Message */}
      {isOverlayOpen && (
        <div className="fixed top-4 right-4 z-[100] animate-slide-in">
          <div className="bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg">
            <p className="text-sm">{overlayMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}